/**
 * Slug Generator Utility
 * Generates short, unique, URL-friendly slugs for job postings
 */

/**
 * Generate a short, random alphanumeric slug (8 characters)
 * @returns {string} Random slug like 'abc123xy'
 */
export const generateShortSlug = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let slug = '';
  for (let i = 0; i < 8; i++) {
    slug += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return slug;
};

/**
 * Generate a URL-friendly job slug from job title and random suffix
 * Examples:
 *   "Senior Developer Role" -> "senior-developer-role-xyz789"
 *   "Product Manager (Full-time)" -> "product-manager-fulltime-abc123"
 * 
 * @param {string} jobTitle - The job title to convert
 * @returns {string} URL-friendly slug (max 50 characters)
 */
export const generateJobSlug = (jobTitle) => {
  if (!jobTitle || typeof jobTitle !== 'string') {
    return generateShortSlug();
  }

  // Convert to lowercase and trim
  let titleSlug = jobTitle
    .toLowerCase()
    .trim()
    // Remove special characters, keep only alphanumeric, spaces, and hyphens
    .replace(/[^\w\s-]/g, '')
    // Replace multiple spaces with single hyphen
    .replace(/\s+/g, '-')
    // Remove duplicate hyphens
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '')
    // Limit to 30 characters to leave room for random suffix
    .substring(0, 30);

  // Add random suffix for uniqueness
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  const slug = titleSlug ? `${titleSlug}-${randomSuffix}` : randomSuffix;

  // Final length limit
  return slug.substring(0, 50);
};

/**
 * Validate if a string is a valid UUID format
 * @param {string} id - The ID to validate
 * @returns {boolean} True if valid UUID
 */
export const isValidUUID = (id) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

/**
 * Check if a string looks like a slug (vs a UUID)
 * @param {string} id - The identifier to check
 * @returns {boolean} True if appears to be a slug
 */
export const isSlug = (id) => {
  return !isValidUUID(id) && /^[a-z0-9-]+$/.test(id);
};

/**
 * Determine the lookup field for database query
 * @param {string} identifier - UUID or slug
 * @returns {string} Either 'id' or 'public_slug'
 */
export const getJobLookupField = (identifier) => {
  return isValidUUID(identifier) ? 'id' : 'public_slug';
};

export default {
  generateShortSlug,
  generateJobSlug,
  isValidUUID,
  isSlug,
  getJobLookupField,
};
